import { useState, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { useOfferLayout } from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type { RentalSearchOutcome } from '@voll-intelligence/genui-contract';

/**
 * As saídas de uma busca de carro que não trouxe nada (#5642), abaixo da fala
 * fixa do agente: o fornecedor caiu → "Tentar novamente" (a mesma busca, pelo
 * mesmo Buscar) e "Mudar datas ou horários"; sem carros no período ou retirada
 * cedo demais → só o "Mudar", que vira a ação principal. No celular os botões
 * vão na largura toda, um embaixo do outro — a mesma régua do rodapé de
 * "Melhores carros" (`useOfferLayout`).
 *
 * Um toque trava os dois: a resposta do turno chega depois, e um segundo toque
 * mandaria outra busca pelo mesmo challenge.
 */
export function RentalSearchOutcomeActions({
  outcome,
  disabled,
  onRetry,
  onChange,
}: Readonly<{
  outcome: RentalSearchOutcome;
  disabled: boolean;
  /** Ausente quando o cartão não tem a busca inteira para repetir. */
  onRetry: (() => void) | undefined;
  onChange: () => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const { ref, layout } = useOfferLayout<HTMLDivElement>();
  const [pressed, setPressed] = useState(false);
  const stack = layout === 'stack';
  const size = stack ? 'xl' : 'lg';
  const retry = outcome === 'provider_error' && onRetry;
  const locked = disabled || pressed;
  return (
    <div ref={ref} className={stack ? 'grid gap-2 [&>*]:w-full' : 'flex flex-wrap gap-2'}>
      {retry && (
        <Button
          type="button"
          variant="outline"
          size={size}
          disabled={locked}
          onClick={() => {
            setPressed(true);
            retry();
          }}
        >
          {t('rentalSearch.retry')}
        </Button>
      )}
      <Button
        type="button"
        variant={retry ? 'ghost' : 'outline'}
        size={size}
        disabled={locked}
        onClick={onChange}
      >
        {t('rentalSearch.change')}
      </Button>
    </div>
  );
}
